import React, { useEffect, useState } from "react";
import Wrapper from "../helpers/Wrapper";
import {
  AiOutlineMinus,
  AiOutlinePlus,
  AiOutlineWhatsApp,
} from "react-icons/ai";
import { TbHeart, TbTruckDelivery, TbTruckReturn } from "react-icons/tb";
import SectionHead from "../components/SectionHead";
import Product from "../components/Product/Product";
import DetailsSke from "../components/Skeletons/DetailsSke";
import { apiClient } from "../helpers/Axios";
import { Link, useParams } from "react-router-dom";
import FormatCurrency from "../helpers/FormatCurrency";
import Page404 from "./404";
import { AnimatePresence, motion } from "framer-motion";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DiscountCounter from "../components/DiscountCounter";
import Radio from "../ui/Radio";
import { Oval } from "react-loader-spinner";
import { GoShareAndroid } from "react-icons/go";
import AnimatePage from "../components/AnimatePage";
import Gallery from "../components/ProductDetails/Gallery";
import ProductImages from "../components/ProductDetails/ProductImages";
import { useConfig } from "../context/ConfigContext";
import AccordionTwo from "../components/Accordion/AccordionTwo";
import {
  displayImage,
  ReturnPolicies,
  ShippingPolicies,
  tabs,
} from "../helpers/config";
import DatabaseContent from "../components/DatabaseContent";
import { finalPrice } from "../helpers/finalPrice";
import { Helmet } from "react-helmet";

function ProductDetails() {
  const { slug } = useParams();

  const [product, setProduct] = useState(null);
  const [images, setimages] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(1);

  const [cartQuantity, setCartQuantity] = useState(1);

  const [selectedVariant, setselectedVariant] = useState(null);
  const [customise, setCustomise] = useState(false);

  const [wishlists, setWishlists] = useState([]);

  // Calculate Discount Price
  const price = selectedVariant?.price || product?.base_price;
  const final_price = finalPrice(price, product?.discount);

  // Retrieve wishlist from localStorage on component mount
  useEffect(() => {
    const savedWishlist = JSON.parse(localStorage.getItem("wishlists")) || [];
    setWishlists(savedWishlist);
  }, []);

  // Fetching Product
  const { data, error, isPending } = useQuery({
    queryKey: ["prodetails", slug],
    queryFn: async () => {
      const res = await apiClient.get(`/product/${slug}/single`);
      return res.data?.data;
    },
  });

  // Fetching Recommended Products

  const [recommendedProducts, setRecommendedProducts] = useState([]);

  const { data: recomendedData, isFetching: recommendedDataFetching } =
    useQuery({
      queryKey: [slug],
      queryFn: async () => {
        const res = await apiClient.get("/product/recommended-products", {
          params: { id: data?.id },
        });
        return res.data?.data;
      },
    });

  useEffect(() => {
    setRecommendedProducts(recomendedData || []);
  }, [recomendedData]);

  // Setting Product Data, variants and images
  useEffect(() => {
    setProduct(data || null);
    setimages(data?.images || []);
    setselectedVariant(data?.variants[0] || null);
  }, [data]);

  // Change Page Title
  if (product) {
    document.title = product?.name;
  }

  // Quantity Change
  const handleQuantityChangeIncrease = () => {
    setCartQuantity((prev) => (prev += 1));
  };

  const handleQuantityChangeDecrease = () => {
    if (cartQuantity < 2) {
      setCartQuantity(1);
      return;
    }
    setCartQuantity((prev) => (prev -= 1));
  };

  // order via whatsapp

  const location = window.location;

  const openWhatsapp = () => {
    const number = "+233502835422";
    const text =
      encodeURIComponent(`Hi, I want to buy the following products *${product?.name}* 
            *Price:* *₵${product?.base_price}* *URL:* *${location.href}*`);

    const whatsappURL = `https://wa.me/${number}?text=${text}`;
    window.open(whatsappURL, "_blank");
  };

  // Wishlist Operations

  // Add to wishlist

  const queryClients = useQueryClient();

  const addToWishlist = (slug) => {
    const isThere = wishlists?.some((w) => w === slug);

    if (isThere) {
      const filtered = wishlists?.filter((w) => w !== slug);
      setWishlists(filtered);
      localStorage.setItem("wishlists", JSON.stringify(filtered));
      queryClients.invalidateQueries(["wishlists"]);
    } else {
      const nf = [...wishlists, slug];
      setWishlists(nf);
      localStorage.setItem("wishlists", JSON.stringify(nf));
      queryClients.invalidateQueries(["wishlists"]);
    }
  };

  // handleVariantSelection for a product

  const handleVariantSelection = (size, color, material, type) => {
    const variant = product?.variants?.find(
      (v) =>
        v?.size === size &&
        v?.color === color &&
        v?.material === material &&
        v?.type === type,
    );
    setselectedVariant(variant);
  };

  // Add to cart

  const { setIsMiniCartOpen, isMiniCartOpen } = useConfig();

  const { mutate, isPending: cartPending } = useMutation({
    mutationFn: async (formData) => {
      return apiClient.post("/cart/add", formData);
    },
    onSuccess: () => {
      queryClients.invalidateQueries(["cart"]);
      setIsMiniCartOpen(!isMiniCartOpen);
    },
  });

  const handleAddToCart = () => {
    if (!data?.in_stock) {
      return;
    }

    const v = selectedVariant?.id || null;
    const formData = {
      quantity: cartQuantity || 1,
      product: product?.id,
      variant: v,
      customise,
    };

    mutate(formData);
  };

  // Get Subdtring

  const getSubString = (htmlContent) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    const plainText = tempDiv.innerText || tempDiv.textContent;

    if (plainText.length > 300) {
      return `${plainText.substring(0, 300)}...`;
    } else {
      return plainText;
    }
  };

  // Loading and Error Page

  if (isPending) {
    return (
      <>
        <DetailsSke />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Page404 />
      </>
    );
  }

  return (
    <AnimatePage>
      <div className="w-full relative mt-5 mx-auto max-w-[1400px]">
        <Helmet>
          <title>{`${product?.name} - Ecoshoppe Ghana's No. 1 Online Gift Shop`}</title>
          <meta name="description" content={`${getSubString()}`} />
          <meta
            name="keywords"
            content={`${product?.name}, ecommerce, buy ${product?.name} `}
          />
          <meta
            property="og:title"
            content={`${product?.name} - Ecoshoppe Ghana's No. 1 Online Gift Shop`}
          />
          <meta property="og:description" content={getSubString()} />
          <meta
            property="og:image"
            content={`${displayImage(data?.images[0])}`}
          />
        </Helmet>

        {/* Image Gallery */}
        <AnimatePresence>
          {galleryOpen && (
            <motion.div
              className="bg-white w-full h-full fixed z-[9999999999]"
              initial={{ opacity: 0, zoom: 0.9 }}
              animate={{ opacity: 1, zoom: 1 }}
              transition={{ duration: 0.3 }}
              exit={{ opacity: 0, zoom: 0.9 }}
            >
              <Gallery images={images} callback={() => setGalleryOpen(false)} />
            </motion.div>
          )}
        </AnimatePresence>

        <Wrapper>
          {/* Product Name */}
          <div className="w-full mb-5 flex items-center gap-1.5">
            <Link to="/" className="text-gray-500">
              Home
            </Link>
            {"/"}
            <p className="text-gray-500">{product?.name}</p>
          </div>

          {/* Main  */}
          <div className="w-full flex-col flex lg:flex-row gap-x-8 gap-y-5">
            {/* Product Images */}
            <div className="w-full relative overflow-hidden">
              <ProductImages
                callback={() => setGalleryOpen(true)}
                images={images}
              />
            </div>

            {/*============================================================= Product Details ============================================================*/}

            <div className="w-full relative">
              {/* Discount */}

              {product?.discount ? (
                <div className="mb-4">
                  <DiscountCounter relative number={product?.discount} />
                </div>
              ) : null}

              {/* Product Name  and price */}
              <div className="w-full">
                <h1 className="text-3xl sm:text-3xl xl:text-[34px] text-[#121212]">
                  {product?.name}
                </h1>

                <div className="mt-2 flex items-center gap-3 md:mt-5">
                  <h1 className="text-2xl lg:text-[26px] font-medium">
                    <FormatCurrency amount={final_price || 0} />
                  </h1>
                  {product?.discount ? (
                    <h1 className="text-gray-400 line-through text-lg">
                      <FormatCurrency
                        amount={selectedVariant?.price || product?.base_price}
                      />
                    </h1>
                  ) : null}
                </div>

                <p className="mt-2 text-gray-500 sm:mt-3 md:mt-5">
                  {getSubString(product?.description)}
                </p>
              </div>

              {/* Product Variants If Any */}
              {product?.variants?.length ? (
                <div className="w-full mt-4">
                  {/* Colors */}
                  {product?.uniqueColors?.length ? (
                    <div className="w-full">
                      <p className="font-medium capitalize">
                        Colors: {selectedVariant?.color}
                      </p>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {product?.uniqueColors?.length &&
                          product?.uniqueColors?.map((color, i) => (
                            <button
                              onClick={() =>
                                handleVariantSelection(
                                  selectedVariant?.size,
                                  color,
                                  selectedVariant?.material,
                                  selectedVariant?.type,
                                )
                              }
                              key={i}
                              style={{ backgroundColor: color }}
                              className={`w-6 h-6 border rounded-full ring-1 ring-offset-2 transition delay-150
                                                            ${selectedVariant?.color === color ? "ring-black" : "ring-transparent"}
                                                            `}
                            />
                          ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Sizes */}
                  {product?.uniqueSizes?.length ? (
                    <div className="w-full mt-6">
                      <p className="font-medium capitalize">
                        Size: {selectedVariant?.size}
                      </p>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {product?.uniqueSizes?.map((size, i) => (
                          <button
                            key={i}
                            onClick={() =>
                              handleVariantSelection(
                                size,
                                selectedVariant?.color,
                                selectedVariant?.material,
                                selectedVariant?.type,
                              )
                            }
                            className={`px-2 rounded transition delay-150 size-10 border ${selectedVariant?.size === size ? "bg-black text-white" : null}`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Materials */}
                  {product?.uniqueMaterials?.length ? (
                    <div className="w-full mt-6">
                      <p className="font-medium capitalize">
                        Material: {selectedVariant?.material}
                      </p>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {product?.uniqueMaterials?.map((material, i) => (
                          <button
                            key={i}
                            onClick={() =>
                              handleVariantSelection(
                                selectedVariant?.size,
                                selectedVariant?.color,
                                material,
                                selectedVariant?.type,
                              )
                            }
                            className={`px-2 rounded transition-all h-10 border ${selectedVariant?.material === material ? "bg-black text-white" : null}`}
                          >
                            {material}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Types */}
                  {product?.uniqueTypes?.length ? (
                    <div className="w-full mt-6">
                      <p className="font-medium capitalize">
                        Type: {selectedVariant?.type}
                      </p>
                      <div className="flex flex-wrap gap-3 mt-2">
                        <select
                          name=""
                          id=""
                          value={selectedVariant?.type}
                          className="px-4 py-2 border max-w-[200px] w-full rounded"
                          onChange={(e) =>
                            handleVariantSelection(
                              selectedVariant?.size,
                              selectedVariant?.color,
                              selectedVariant?.material,
                              e.target.value,
                            )
                          }
                        >
                          {product?.uniqueTypes?.map((type, i) => (
                            <option key={i} value={`${type}`}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Customization Place */}

              {product?.customable && (
                <div className="w-full mt-4 md:mt-7">
                  {/* radio to select if yes or no */}
                  <div className="flex gap-4 text-sm font-medium">
                    <Radio
                      checked={customise}
                      onChange={() => setCustomise(true)}
                      amount={product?.customise_price}
                      title="Customise"
                    />
                    <Radio
                      onChange={() => setCustomise(false)}
                      checked={!customise}
                      amount={0}
                      title="No Customise"
                    />
                  </div>
                </div>
              )}

              {/* Cart Actions */}
              <div
                className="w-full relative flex flex-col mt-7 lg:mt-9 gap-3 md:flex-row 
                            xl:items-center lg:flex-col xl:flex-row lg:gap-y-8"
              >
                {/* Quantity */}
                <div
                  className="px-8 py-3 bg-gray-100 border border-gray-200 
                                        rounded-full flex items-center justify-between"
                >
                  <button onClick={() => handleQuantityChangeDecrease()}>
                    <AiOutlineMinus />
                  </button>
                  <input
                    type="text"
                    onChange={(e) => {
                      const qty = e.target.value;
                      setCartQuantity(qty || 1);
                    }}
                    className="bg-transparent w-20 outline-none text-center"
                    value={cartQuantity}
                  />
                  <button onClick={() => handleQuantityChangeIncrease()}>
                    <AiOutlinePlus />
                  </button>
                </div>

                {/* Add to cart & Fav   */}
                <div className="flex gap-3 w-full items-center">
                  <button
                    disabled={!product?.in_stock}
                    onClick={handleAddToCart}
                    className="bg-slate-900 px-10 lg:px-10 py-3.5 rounded-full border 
                                    text-xs sm:text-sm shadow text-white transition-all w-full truncate flex justify-center items-center"
                  >
                    {cartPending ? (
                      <Oval
                        color="white"
                        strokeWidth={3}
                        secondaryColor="white"
                        width={20}
                        height={20}
                      />
                    ) : (
                      "ADD TO CART"
                    )}
                  </button>

                  <div className="flex gap-3">
                    <button
                      onClick={() => addToWishlist(product?.slug)}
                      title="Add to WishList"
                      className={`w-12 h-12 rounded-full border
                                          ${wishlists?.some((w) => w === data?.slug) ? "bg-black text-white" : "bg-white"}
                                        hover:text-white transition-all flex justify-center items-center hover:bg-black`}
                    >
                      <TbHeart />
                    </button>

                    <button
                      title="Share"
                      className="w-12 h-12 rounded-full border
                                        hover:text-white transition-all flex justify-center items-center hover:bg-black"
                    >
                      <GoShareAndroid />
                    </button>
                  </div>
                </div>
              </div>

              {/* Buy Now Button */}
              <div className="w-full mt-5 flex flex-col gap-3 md:flex-row md:items-center">
                <button
                  onClick={openWhatsapp}
                  className=" bg-green-700 px-10 lg:px-10 py-4 rounded-full border flex items-center gap-2 
                                    text-xs text-white uppercase transition-all w-full font-medium tracking-wide justify-center"
                >
                  <AiOutlineWhatsApp size={16} />
                  Order via whatsapp
                </button>
              </div>

              {/* Delivery Info and others  */}
              <div className="w-full space-y-3 mt-8">
                <div className="flex items-center gap-5">
                  <TbTruckDelivery className="text-gray-400" size={25} />
                  <div className="text-gray-500">
                    Estimate delivery times: 3-6 days (International)
                  </div>
                </div>

                <div className="flex items-center gap-5">
                  <TbTruckReturn className="text-gray-400" size={25} />
                  <div className="text-gray-500">
                    Return within 45 days of purchase. Duties & taxes are
                    non-refundable.
                  </div>
                </div>
              </div>

              <hr className="my-5" />

              {/* Category */}
              <div className="w-full space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-gray-500">Available:</p>
                  <p>{product?.in_stock ? "Instock" : "Out of stock"}</p>
                </div>

                <div className="flex items-center gap-2">
                  <p className="text-gray-500">Category:</p>
                  <Link
                    to={`/product-category/${product?.subcategory?.category?.name?.toLowerCase()}`}
                  >
                    {product?.subcategory?.category?.name}
                  </Link>
                </div>
              </div>

              {/* Safe Checkout */}
            </div>
            <hr className="mt-10" />
          </div>

          {/* Description, Shipping Policies and Retuurn Policies */}

          {/* Mobile */}

          <div className="w-full relative xl:hidden p-3 flex flex-col gap-3">
            <AccordionTwo title="Description">
              <DatabaseContent htmlContent={product?.description} />
            </AccordionTwo>
            <AccordionTwo title="Shipping & Returns">
              <ShippingPolicies />
            </AccordionTwo>
            <AccordionTwo title="Return Policies">
              <ReturnPolicies />
            </AccordionTwo>
          </div>

          {/* Large Sceen */}
          {/* Tabs */}
          <div className="hidden xl:block">
            <div className="flex items-center gap-5 mt-16 justify-center">
              {tabs.map((tab, i) => (
                <button
                  key={i}
                  className={`bg-${
                    currentTab === i + 1 ? "black" : "gray-100"
                  } text-${
                    currentTab === i + 1 ? "white" : "black"
                  } p-3 rounded-full px-8 font-medium transition-all ease-linear`}
                  onClick={() => setCurrentTab(i + 1)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="w-full mt-8">
              {/* Description Tab */}
              {currentTab === 1 && (
                <DatabaseContent htmlContent={product?.description} />
              )}
              {/* Shipping & Return */}

              {currentTab === 2 && <ShippingPolicies />}

              {/* Return Policy */}
              {currentTab === 3 && <ReturnPolicies />}
            </div>
          </div>

          {/* Recommended Products */}
          <div className="w-full relative mt-16">
            <SectionHead
              title={"Recommended Products"}
              description="Discover similar products tailored to your preferences."
            />

            {/* Products */}
            <div className="w-full">
              <div className="grid grid-cols-2 mt-8 gap-3 sm:gap-8 lg:grid-cols-4">
                {recommendedProducts.map((product, i) => (
                  <div key={i}>
                    <Product loading={recommendedDataFetching} data={product} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Wrapper>
      </div>
    </AnimatePage>
  );
}

export default ProductDetails;
